import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { WebsocketContext } from 'src/utils/apis/WebsocketContext';
import { demoEngine } from './engine';

const MESSAGE_FLUSH_DELAY = 500;
const MESSAGE_FLUSH_LIMIT = 1000;

export const DemoWebsocketProvider = ({ children }: { children: JSX.Element | JSX.Element[] | string }) => {
  const [isReady, setIsReady] = useState(false);
  const [messages, setMessages] = useState<string[]>([]);
  const subs = useRef(new Map<string, () => void>()); // topic -> unsubscribe
  const counts = useRef(new Map<string, number>());
  const buffer = useRef<string[]>([]);
  const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;
    demoEngine.boot().then(() => {
      if (!cancelled) setIsReady(true);
    });
    return () => { cancelled = true; };
  }, []);

  const scheduleFlush = useCallback(() => {
    if (flushTimer.current) return;
    flushTimer.current = setTimeout(() => {
      if (buffer.current.length > MESSAGE_FLUSH_LIMIT) {
        buffer.current.splice(0, buffer.current.length - MESSAGE_FLUSH_LIMIT);
      }
      setMessages([...buffer.current]);
      buffer.current.length = 0;
      flushTimer.current = null;
    }, MESSAGE_FLUSH_DELAY);
  }, []);

  const addListener = useCallback((topic: string) => {
    const unsub = demoEngine.subscribe(topic, (frame) => {
      buffer.current.push(frame);
      scheduleFlush();
    });
    subs.current.set(topic, unsub);
  }, [scheduleFlush]);

  const subscribeTopic = useCallback((topic: string) => {
    const count = counts.current.get(topic) ?? 0;
    counts.current.set(topic, count + 1);
    if (count === 0) addListener(topic);
  }, [addListener]);

  const unsubscribeTopic = useCallback((topic: string) => {
    const count = counts.current.get(topic) ?? 0;
    if (count <= 1) {
      const off = subs.current.get(topic);
      off && off();
      subs.current.delete(topic);
      counts.current.delete(topic);
    } else {
      counts.current.set(topic, count - 1);
    }
  }, []);

  const consumeMessages = useCallback((count: number) => {
    setMessages(prev => prev.slice(count));
  }, []);

  const contextValue = useMemo(() => ({
    isReady,
    messages,
    consumeMessages,
    subscribeTopic,
    unsubscribeTopic,
  }), [isReady, messages, consumeMessages, subscribeTopic, unsubscribeTopic]);

  return (
    <WebsocketContext.Provider value={contextValue}>
      {children}
    </WebsocketContext.Provider>
  );
};
